import React from "react";
import { X, AlertTriangle } from "lucide-react";

interface PopupBlockerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PopupBlockerModal: React.FC<PopupBlockerModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="text-center mb-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-yellow-100 mb-4">
            <AlertTriangle className="h-8 w-8 text-yellow-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900">Popup Blocked</h3>
          <p className="mt-2 text-sm text-gray-500">
            Your browser has blocked the PDF popup. Please enable popups for this site to download your prescription.
          </p>
        </div>
        
        <div className="rounded-md bg-gray-50 p-4">
          <div className="mb-4">
            <svg className="w-full h-64" viewBox="0 0 400 260" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Browser frame */}
              <rect x="20" y="20" width="360" height="220" rx="8" fill="#F9FAFB" stroke="#E5E7EB" strokeWidth="2" />
              
              {/* Browser toolbar */}
              <rect x="20" y="20" width="360" height="36" rx="8" fill="#F3F4F6" stroke="#E5E7EB" strokeWidth="2" />
              <circle cx="40" cy="38" r="6" fill="#EF4444" />
              <circle cx="60" cy="38" r="6" fill="#F59E0B" />
              <circle cx="80" cy="38" r="6" fill="#10B981" />
              
              {/* URL bar */}
              <rect x="100" y="30" width="240" height="16" rx="4" fill="white" stroke="#D1D5DB" strokeWidth="1" />
              <circle cx="112" cy="38" r="4" fill="#6B7280" />
              
              {/* Popup blocker notification */}
              <rect x="230" y="70" width="130" height="80" rx="6" fill="white" stroke="#6B7280" strokeWidth="2" />
              <rect x="240" y="80" width="110" height="40" rx="4" fill="#FEF3C7" />
              <path d="M272 95L272 105M272 113L272 113" stroke="#D97706" strokeWidth="3" strokeLinecap="round" />
              <circle cx="272" cy="100" r="12" fill="#FBBF24" opacity="0.3" />
              <text x="290" y="100" fontFamily="sans-serif" fontSize="10" fill="#92400E">Popup blocked</text>
              
              {/* Allow button */}
              <rect x="240" y="130" width="50" height="16" rx="4" fill="#4ADE80" />
              <text x="248" y="142" fontFamily="sans-serif" fontSize="10" fill="white">Allow</text>
              
              {/* Popup window (grayed out) */}
              <rect x="100" y="90" width="120" height="140" rx="6" fill="#F3F4F6" stroke="#D1D5DB" strokeWidth="2" opacity="0.7" />
              <rect x="110" y="100" width="100" height="12" rx="2" fill="#D1D5DB" />
              <rect x="110" y="120" width="100" height="80" rx="2" fill="#E5E7EB" />
              <rect x="140" y="210" width="40" height="12" rx="2" fill="#D1D5DB" />
            </svg>
          </div>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-800">How to enable popups:</h4>
              <ol className="mt-2 ml-4 text-sm text-gray-600 list-decimal space-y-2">
                <li>Look for the popup blocked icon <span className="inline-block w-4 h-4 bg-gray-200 rounded-full text-center text-xs leading-4">🚫</span> in your browser's address bar</li>
                <li>Click on the icon and select "Allow popups from this site"</li>
                <li>Try downloading the PDF again</li>
              </ol>
            </div>
            
            <div className="pt-2">
              <p className="text-xs text-gray-500">
                Different browsers may display this notification differently. Check your browser's settings if you can't find the popup blocker icon.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
            onClick={onClose}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopupBlockerModal;